// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
/**
 * What the editor asks of Harvest. Components take this interface rather
 * than calling Apollo themselves, so stories and tests hand them a fake and
 * the production hook ({@link useMonitoringEditorApi}) is the only place that
 * knows about GraphQL.
 */
import { parseActionResponseBody } from "@/services/graphqlActionError";
import { normalizeProblems } from "@/components/monitoring/lib/problems";
import { EMonitoringErrorCode } from "@/components/monitoring/lib/errors";
import { EMonitoringSaveStatus, } from "./types";
/** Earlier spellings, still read so an older Harvest keeps working. */
var CONFLICT_CODES = new Set([EMonitoringErrorCode.CONFLICT, "conflict", "CONFLICT"]);
var INVALID_CODES = new Set([
    EMonitoringErrorCode.INVALID,
    "invalid",
    "INVALID",
    "unprocessable",
    "validation-failed",
]);
var HARVEST_CODES = new Set(Object.values(EMonitoringErrorCode));
var CONFLICT_STATUS = 409;
var INVALID_STATUS = 422;
/** What the editor says for a refusal that needs no more than a message. */
var ERROR_MESSAGES = (_a = {},
    _a[EMonitoringErrorCode.CHECKS_UNAVAILABLE] = "monitoring.editor.errors.checksUnavailable",
    _a[EMonitoringErrorCode.BUSY] = "monitoring.editor.errors.busy",
    _a[EMonitoringErrorCode.LOCKED_DOWN] = "monitoring.editor.errors.lockedDown",
    _a[EMonitoringErrorCode.FORBIDDEN_SCOPE] = "monitoring.editor.errors.forbiddenScope",
    _a[EMonitoringErrorCode.BAD_REQUEST] = "monitoring.editor.errors.badRequest",
    _a);
var record = function (value) {
    return value && typeof value === "object" ? value : {};
};
var authorOf = function (value) {
    if (typeof value === "string")
        return { id: value };
    var author = record(value);
    return typeof author.id === "string"
        ? { id: author.id, name: typeof author.name === "string" ? author.name : null }
        : null;
};
/**
 * Each refusal in a failed action, wherever Hasura put Harvest's answer
 * (`{message, extensions: {code, ...}}`): promoted into the GraphQL error's
 * extensions, or left in the original response body.
 */
var refusals = function (error) {
    var _a;
    var actionError = error;
    return ((_a = actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) !== null && _a !== void 0 ? _a : []).map(function (graphQLError) {
        var _a, _b;
        var extensions = record(graphQLError.extensions);
        var internal = record(extensions.internal);
        var response = record(internal.response);
        var body = record(parseActionResponseBody(response.body));
        var bodyExtensions = record(body.extensions);
        var promoted = typeof extensions.code === "string" && extensions.code !== "unexpected";
        return {
            code: String((_b = (_a = (promoted ? extensions.code : bodyExtensions.code)) !== null && _a !== void 0 ? _a : extensions.code) !== null && _b !== void 0 ? _b : ""),
            status: typeof response.status === "number" ? response.status : undefined,
            details: __assign(__assign(__assign({}, body), bodyExtensions), extensions),
        };
    });
};
/** Harvest's code for a failed action, when it gave one. */
export var monitoringErrorCode = function (error) { var _a; return (_a = refusals(error).find(function (refusal) { return refusal.code; })) === null || _a === void 0 ? void 0 : _a.code; };
/**
 * The translation key of what to tell the author about a failure Harvest
 * explained (busy, locked down, …); `undefined` when the reason is to be shown as is.
 */
export var monitoringErrorMessage = function (error) {
    var code = monitoringErrorCode(error);
    return code ? ERROR_MESSAGES[code] : undefined;
};
var revisionOf = function (value) {
    if (value === null || value === undefined || value === "")
        return null;
    var revision = Number(value);
    return Number.isFinite(revision) ? revision : null;
};
/**
 * A save Harvest refused: 409 carries `{current_revision, author, time}`
 * and 422 `{problems}`. `undefined` for any other failure.
 */
export var interpretSaveError = function (error) {
    for (var _i = 0, _a = refusals(error); _i < _a.length; _i++) {
        var _b = _a[_i], code = _b.code, status_1 = _b.status, details = _b.details;
        if (CONFLICT_CODES.has(code) || status_1 === CONFLICT_STATUS) {
            return {
                status: EMonitoringSaveStatus.CONFLICT,
                current_revision: revisionOf(details.current_revision),
                author: authorOf(details.author),
                time: typeof details.time === "string" ? details.time : null,
            };
        }
        // A 422 is read as problems only when Harvest named no code of its
        // own: a MONITORING_BAD_REQUEST is a request it could not read, not
        // a document it found problems in.
        if (INVALID_CODES.has(code) || (status_1 === INVALID_STATUS && !HARVEST_CODES.has(code))) {
            return {
                status: EMonitoringSaveStatus.INVALID,
                problems: normalizeProblems(details.problems),
            };
        }
    }
    return undefined;
};
